import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'

import * as PoziUtils from 'api/utils'
import PoziNotifications from 'utils/notifications'

import { msalConfig } from './msalAuthConfig'

// Keep track of whether an MSAL response has been handled
// This can be only called once (to prevent errors like 'ClientAuthError: state_not_found: State not found:  Cached State')
let msalResponseHandled = false

let msalInstance: PublicClientApplication

let msalTokenFirstRequestDone = false

export const doMsal = async () => {
  console.debug('MSAL - doMSal init')

  // Usge global 'getAppInfo' from index.html
  const appInfo = window.getAppInfo()

  const siteName = appInfo.site
  const isEnterprise = appInfo.enterprise
  const isStaging = appInfo.environment === 'staging'

  // TODO: find a better way to retrieve the config
  const azureConf = window.appObject?.siteConfig?.azure || {}

  let {
    authority,
    clientId,
    scopes = [],
    authType = 'token',
    enabled = false,
    redirectUri
  } = azureConf

  console.debug('MSAL settings', siteName, enabled, isEnterprise, authType, azureConf)

  // let clientId // also called application id
  // let authority // uses the tenant id
  // let redirectUri // generally no need to set this value
  // let scopes: string[] = [] // for token based access! Needs '.../user_impersonation'!
  // let tenantId // for use in authority

  // Detect if there may be a login through redirect in progress.
  // This happens when there is something like #code=... in the URL
  // We get this information from the session storage in the field 'msal.interaction.status'
  if (!clientId) {
    clientId = sessionStorage.getItem('msal.interaction.status')
    console.log('MSAL - No clientId could be found in session storage. Not proceeding. Site:', siteName)
  }

  // Stop processing if no client id has been provided
  if (!clientId) {
    console.log('MSAL - No clientId provided nor could be found in session storage. Not proceeding. Site:', siteName)
    return
  }

  if (clientId) {
    msalConfig.auth.clientId = clientId
  }

  if (authority) {
    msalConfig.auth.authority = authority
  }

  // When we are in staging, tell Azure to return to the root of staging after successful login
  if (isStaging) {
    redirectUri = `${window.location.origin}/`
  }

  if (redirectUri) {
    msalConfig.auth.redirectUri = redirectUri
  }

  // Create the main myMSALObj instance
  // configuration parameters are located at msalAuthConfig
  msalInstance = new PublicClientApplication(msalConfig)

  // Need to wait for initialisation to complete (since v3.0)
  await msalInstance.initialize()

  let accountId = ''

  const loginRequest = {
    scopes
    // extraScopesToConsent: ['api://pozicloudserver/iis', 'api://pozicloudserver/userimpersonation']
  }

  const acquireToken = async (request: any, account: any) => {
    request.account = account

    // console.debug('MSAL attempting silent token acquisition...', account)

    return msalInstance.acquireTokenSilent(request).catch(async (error) => {
      const { errorCode, errorMessage, subError, name } = error

      console.debug('MSAL silent token acquisition fails.', errorCode, error)

      const shouldAcquireTokenExplicitly = error instanceof BrowserAuthError || error instanceof InteractionRequiredAuthError
      if (shouldAcquireTokenExplicitly) {
        console.debug('MSAL - Error is InteractionRequiredAuthError.', errorCode, Object.keys(error), error)

        if (errorCode === 'login_required') {
          console.debug('MSAL silent token failure: login required', errorCode, error)
        }
        console.debug('MSAL attempting acquiring token using popup')
        return msalInstance.acquireTokenPopup(request).catch(error => {
          console.log('MSAL token popup request error', error)

          const { errorCode } = error
          if (errorCode !== 'popup_window_error') {
            console.log('MSAL token request error is not popup window. Not sure what to do now. Not redirecting!', errorCode, error)
            return
          } else {
            console.log('MSAL token request error was popup being blocked. Attempting to resolve by redirecting.')
          }

          return msalInstance.acquireTokenRedirect(request).catch(async error => {
            console.log('MSAL token redirect request error', error)

            const { errorCode } = error

            if (errorCode === 'redirect_in_iframe') {
              console.log('MSAL - running in iframe. Popups must be allowed for this.', errorCode, error)

              let parentLocation = 'this site'

              const referrer = window.document.referrer
              if (referrer) {
                try {
                  const referrerUrl = new URL(referrer)
                  parentLocation = referrerUrl.hostname
                } catch (_error) {}
              }

              PoziNotifications().error(`Please allow pop-ups for ${parentLocation} and refresh the page to complete the authentication process.`, {
                autoHideDuration: null // disable auto hide
              })

              // Allow for the user to read the error message before a possible redirect happens
              await PoziUtils.sleep(30000)
            }

            throw new Error(error)
          })
        })
      }

      let message = `Error: ${errorCode} - ${errorMessage}`

      switch (errorCode) {
        case 'endpoints_resolution_error': {
          console.log('MSAL - Error - endpoints_resolution_error', error, request)
          console.warn('MSAL - PLEASE CHECK CONTENTS OF FAILED REQUEST(S).')

          break
        }

        case 'invalid_grant': {
          // This can happen when the token has expired. Normally, the library tries to recover from
          // this by creating an iframe and requesting a new token.
          // Example error:
          //   {
          //     "error": "invalid_grant",
          //     "error_description": "AADSTS700084: The refresh token was issued to a single page app (SPA), and therefore has a fixed, limited lifetime of 1.00:00:00, which cannot be extended. It is now expired and a new sign in request must be sent by the SPA to the sign in page. The token was issued on 2023-12-09T00:47:06.5945447Z. Trace ID: xxxxxxxx Correlation ID: xxxxxx Timestamp: 2023-12-10 02:20:54Z",
          //     "error_codes": [
          //         700084
          //     ],
          //     "timestamp": "2023-12-10 02:20:54Z",
          //     "trace_id": "xxxxxx",
          //     "correlation_id": "xxxxxx",
          //     "error_uri": "https://login.microsoftonline.com/error?code=700084",
          //     "suberror": "bad_token"
          //   }
          //
          // At the time of writing (2023-12-11), we have not seen this error yet.
          console.log('MSAL - Invalid grant error', errorCode, error)

          message = `Authentication session expired: ${errorCode} - ${errorMessage}`

          break
        }

        case 'invalid_request': {
          break
        }

        case 'no_account_error': {
          console.log('MSAL - No active account has been set (yet).', errorCode, error, account)
          break
        }

        default: {
          console.error('MSAL - Unknown error', errorCode, error)

          message = `Unexpected error: ${errorCode} - ${errorMessage}`
        }
      }

      // Show error in notification snackbar
      PoziNotifications().error(message, {
        autoHideDuration: null // disable auto hide
      })

      // Allow for the user to read the error message before a possible redirect happens
      await PoziUtils.sleep(30000)

      throw error
    })
  }

  const getToken = async () => {
    if (!msalTokenFirstRequestDone) {
      console.debug('MSAL - getToken... first request?', msalTokenFirstRequestDone)
    }

    const account = getAccount()

    // Only force refresh on first request
    // See: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md#avoiding-interactive-interruptions-in-the-middle-of-a-users-session
    const forceRefresh = !msalTokenFirstRequestDone

    // Create token request object
    const tokenRequest = {
      scopes,
      forceRefresh
    }

    const response = await acquireToken(tokenRequest, account).catch(error => {
      console.log('MSAL - getToken - acquireToken error', error.errorCode, error)
    })

    // console.debug('MSAL token response', response)

    if (response) {
      // Need to create subtype for idTokenClaims.
      // See: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/3870#issuecomment-883495860
      // and: https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/2084#issuecomment-675013771

      type IdTokenClaims = {
        groups: string[]
        roles: string[]
      }

      // Show extra debugging output when token is new / refreshed
      if (!response.fromCache) {
        const tokenStatus = msalTokenFirstRequestDone ? 'REFRESH' : 'NEW'
        console.debug(`MSAL - getToken - token - ${tokenStatus}`, response)

        const idTokenClaims = response.idTokenClaims as IdTokenClaims

        console.debug('MSAL - getToken token ROLES', idTokenClaims.roles)
        console.debug('MSAL - getToken token SCOPES', response.scopes)
        console.debug('MSAL - getToken token GROUPS', idTokenClaims.groups)
        console.debug('MSAL - getToken User account', account)

        const currentDate = new Date()
        const tokenExpiryDate = response.expiresOn

        console.debug('MSAL - getToken - current date/time', currentDate)
        console.debug('MSAL - getToken - tokenExpiryDate / time', tokenExpiryDate)
      } else {
        // console.debug('MSAL - getToken - response from cache', response)
      }

      // Mark first token request as done
      if (!msalTokenFirstRequestDone) {
        msalTokenFirstRequestDone = true
      }
    } else {
      console.log('MSAL - getToken - no response')
    }

    return response
  }

  const handleResponseRedirect = (response: any) => {
    console.debug('MSAL handleResponseRedirect', response)

    // Once MSAL retrieves the original URL from the cache, store the
    // active account and reload Pozi to reflect the previous state of the app
    if (response) {
      console.debug(`MSAL Welcome ${response?.account?.username}`)

      setActiveAccount(response)

      // All going well, the URL would have been updated but will need to reload
      // in order to bring the site back to where it was before logging in
      window.location.reload()
    }
  }

  const handleResponsePopup = (response: any) => {
    console.warn('MSAL handleResponsePopup', response)
    // console.warn(`MSAL Welcome ${response?.account?.username}`)

    setActiveAccount(response)
  }

  // Setting active account is needed for silent token acquisition later
  const setActiveAccount = (response: any) => {
    if (response !== null) {
      console.debug('MSAL - setActiveAccount - response:', response)

      msalInstance.setActiveAccount(response.account)
      accountId = response.account.homeAccountId
    } else {
      console.debug('MSAL - setActiveAccount - no response')

      // need to call getAccount here?
      const allAccounts = msalInstance.getAllAccounts()
      console.debug('MSAL - setActiveAccount - allAccounts?', allAccounts)

      // TODO: do we need this bit of code?
      if (!allAccounts || allAccounts.length < 1) {
        // Add something here
      } else if (allAccounts.length > 1) {
      // Add choose account code here
      } else if (allAccounts.length === 1) {
        msalInstance.setActiveAccount(allAccounts[0])
        accountId = allAccounts[0].homeAccountId
      }
    }

    console.debug('MSAL set account id', accountId)
  }

  // Redirect: once login is successful, process the #code=... part of the URL
  // and retrieve the original path from the cache and redirect to it
  // NOTE: this currently does not work perfectly.

  const msalHandleResponseRedirect = () => {
    // TODO: handle errors like this:
    // http://localhost:3000/#error=invalid_resource&error_description=AADSTS500011%3a+The+resource+principal+named+https%3a%2f%2fpozi-cardiniavicgovau.msappproxy.net+was+not+found+in+the+tenant+named+d1ea9051-cc51-4556-8729-e42a26edf37e.+This+can+happen+if+the+application+has+not+been+installed+by+the+administrator+of+the+tenant+or+consented+to+by+any+user+in+the+tenant.+You+might+have+sent+your+authentication+request+to+the+wrong+tenant.%0d%0aTrace+ID%3a+e590fa65-5f8e-467b-b45e-8acc3c211600%0d%0aCorrelation+ID%3a+5f1bd81c-71a4-45a3-8015-282a976a8d5d%0d%0aTimestamp%3a+2022-10-30+21%3a55%3a10Z&error_uri=https%3a%2f%2flogin.microsoftonline.com%2ferror%3fcode%3d500011&state=eyJpZCI6ImJjZGQ0NGUxLTJkZWItNDhmOS04NWUyLThiOThiOTkyNjgwOSIsIm1ldGEiOnsiaW50ZXJhY3Rpb25UeXBlIjoicmVkaXJlY3QifX0%3d
    console.debug('MSAL - calling handleRedirectResponse...')
    msalInstance.handleRedirectPromise()
      .then(handleResponseRedirect)
      .catch(error => {
        const { errorCode, errorMessage, subError, name } = error

        console.error('MSAL responseRedirect error', error)

        console.error('MSAL Error: Invalid Client errorCode', errorCode)
        console.error('MSAL Error: Invalid Client name', name)
        console.error('MSAL Error: Invalid Client errorMessage', errorMessage)
        console.error('MSAL Error: Invalid Client subError', subError)

        // Hack to make sure the user sees it before redirect
        PoziNotifications().error(`Unexpected error: ${errorMessage} (${errorCode})`)
        // throw error
      })
  }

  // var silentRequest = {
  //   scopes: ['User.Read', 'profile'],
  //   // account: currentAccount,
  //   forceRefresh: false
  // }

  const signIn = async (type = 'popup') => {
    console.debug('MSAL - signIn', type)

    if (type === 'popup') {
      return msalInstance.loginPopup(loginRequest).then(handleResponsePopup).catch(async (error: any) => {
        console.error('MSAL sign in popup request error', error, typeof error, error.errorCode, { ...error })
        const errorCode = error.errorCode
        if (errorCode !== 'popup_window_error') {
          console.warn('MSAL login error is not popup window. Not redirecting!')
          return
        } else {
          console.log('MSAL login error was popup being blocked. Attempting to resolve by redirecting.', errorCode)
        }

        // Error was likely popup window being blocked, let's try redirect instead.
        // alert('In order to login, please allow popup windows and reload')
        try {
          return await msalInstance.loginRedirect(loginRequest)
        } catch (error2) {
          console.error('MSAL sign in redirect request error', error2)
        }
      })
    } else if (type === 'redirect') {
      return msalInstance.loginRedirect(loginRequest)
    }
  }

  const signOut = async (type = 'popup') => {
    console.debug('MSAL - signOut - instance', msalInstance)

    const logoutRequest = {
      // account: msalInstance.getAccountByHomeId(accountId)
    }
    if (type === 'popup') {
      try {
        await msalInstance.logoutPopup(logoutRequest)
      } catch (error) {
        console.log('MSAL sign out popup request error. Trying redirect.', error)

        await msalInstance.logoutRedirect(logoutRequest)
      }
    } else {
      await msalInstance.logoutRedirect(logoutRequest)
    }
  }

  // @ts-expect-error
  window.msalInstance = msalInstance; window.signIn = signIn; window.signOut = signOut; window.accountId = accountId

  // @ts-expect-error
  window.getToken = getToken; window.doMsal = doMsal; window.msalHandleResponseRedirect = msalHandleResponseRedirect

  // @ts-ignore
  window.msalConfig = msalConfig

  // console.warn('MSAL - Run \'await msalInstance.loginPopup()\' or \'await msalInstance.loginRedirect()\' for login.')
  // console.warn('MSAL - Run \'await msalInstance.logoutPopup()\' or \'await msalInstance.logoutRedirect()\' for logout.')

  // Process any outstanding login redirect reponses (like #code=...)
  // But only call this once (to prevent errors like 'ClientAuthError: state_not_found: State not found:  Cached State')
  if (!msalResponseHandled) {
    msalResponseHandled = true

    msalHandleResponseRedirect()
  } else {
    console.warn('MSAL - Blocked attempt to call handleRedirectPromise() more than once!')
  }

  return msalInstance
}

export const getAccount = () => {
  if (!msalInstance) {
    console.log('MSAL - getAccount - no instance')

    return null
  }

  let account = msalInstance.getActiveAccount()
  if (account) {
    // console.debug('MSAL - getAccount - active account found', account)
    return account
  }

  console.debug('MSAL - getAccount - no active account found. Trying other options...')

  // Find the first account in all accounts that matches the tenant ID of the authority
  const allAcounts = msalInstance.getAllAccounts()
  const { authority } = msalConfig.auth
  account = allAcounts.find(account => authority?.includes(account.tenantId)) || null

  if (account) {
    console.debug('MSAL - getAccount - account found by matching tenant id of authority. Setting as active account.', account)
    msalInstance.setActiveAccount(account)
    return account
  }

  // Fallback in case any of the above methods fail
  // @ts-expect-error
  const accountId = window.accountId
  account = msalInstance.getAccount({ homeAccountId: accountId })
  if (account) {
    console.debug('MSAL - getAccount - account by home account id. Setting as active account.', accountId, account)

    msalInstance.setActiveAccount(account)
    return account
  }

  console.log('MSAL - getAccount - no account found. Giving up.', accountId)

  return null
}

export const getAccountGroups = () => {
  const user = getAccount()

  if (!user) {
    console.log('MSAL - getAccountGroups - user not found')

    return
  }

  const groups = user?.idTokenClaims?.groups as string[] || []

  console.debug('MSAL - getAccountGroups', user?.username, groups)

  return groups
}
